import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, thumbUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LazyImage from "@/components/LazyImage";
import { Box, Check, Search } from "lucide-react";

const norm = (s) => (s || "").toString().normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Paso 1 del modelo: solo se eligen hormas revisadas o liberadas, y activas, del catálogo.
export default function ElegirHormaDialog({ open, onOpenChange, modelo, onElegida }) {
  const [hormas, setHormas] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);
  const actual = modelo?.horma;

  useEffect(() => {
    if (!open) return;
    setQ(""); setMotivo(""); setSel(actual?.elegible ? actual.id : null);
    api.get("/hormas").then((r) => setHormas(r.data.filter((h) => h.elegible))).catch(() => setHormas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const lista = useMemo(() => {
    if (!hormas) return null;
    const t = norm(q.trim());
    if (!t) return hormas;
    const rango = (h) => (norm(h.nombre).includes(t) ? 1 : norm(h.codigo).includes(t) ? 2 : norm(h.proveedor).includes(t) ? 3 : null);
    return hormas.map((h) => [rango(h), h]).filter(([r]) => r).sort((a, b) => a[0] - b[0]).map(([, h]) => h);
  }, [hormas, q]);

  const cambio = actual && sel && sel !== actual.id;
  const listo = sel && (!cambio || motivo.trim().length >= 10);

  const guardar = async () => {
    setBusy(true);
    try {
      await api.post(`/modelos/${modelo.id}/horma`, { horma_id: sel, motivo: cambio ? motivo.trim() : null });
      toast.success(modelo?.ruta_info?.horma_en_paso === false ? "Horma asignada" : "Horma asignada: pasos 1 y 2 completos");
      onOpenChange(false);
      onElegida?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{actual ? "Cambiar horma" : "Elegir horma"}</DialogTitle>
          <DialogDescription>Solo aparecen las hormas revisadas o liberadas, y activas.{modelo?.ruta_info?.horma_en_paso === false ? "" : " Al elegirla se completan los pasos 1 y 2."}</DialogDescription>
        </DialogHeader>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o proveedor" className="pl-9" data-testid="buscar-horma-modelo" />
        </div>
        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {lista === null && <p className="p-4 text-sm text-slate-400">Cargando…</p>}
          {lista?.length === 0 && (
            <p className="p-4 text-sm text-slate-400">{hormas?.length ? "Ninguna horma coincide." : "No hay hormas revisadas. Pide a Desarrollo que dé de alta la horma en el menú Hormas."}</p>
          )}
          {lista?.map((h) => (
            <button key={h.id} onClick={() => setSel(h.id)} data-testid={`opcion-horma-${h.codigo}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${sel === h.id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
              <div className="h-9 w-12 rounded bg-slate-50 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                {h.foto_externa_id ? <LazyImage src={thumbUrl(h.foto_externa_id)} alt={h.nombre} containerClassName="w-full h-full" imgClassName="object-cover" /> : <Box size={14} className="text-slate-300" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-800 truncate">{h.nombre}</div>
                <div className="text-[11px] text-slate-400 truncate"><span className="font-mono">{h.codigo}</span>{h.proveedor && ` · ${h.proveedor}`}{h.genero && ` · ${h.genero}`}</div>
              </div>
              {sel === h.id && <Check size={16} className="text-blue-600 shrink-0" />}
            </button>
          ))}
        </div>
        {cambio && (
          <div className="space-y-1.5">
            <Label>Motivo del cambio de horma (mínimo 10 caracteres)</Label>
            <Textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} data-testid="motivo-cambio-horma" />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={busy || !listo || (actual && sel === actual.id && actual.elegible && (modelo?.ruta_info?.horma_en_paso === false || modelo.pasos?.[0]?.hecho))} data-testid="confirmar-horma">
            {actual ? "Guardar" : "Elegir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
